import { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { ShieldCheck, AlertTriangle, XCircle, Upload, CheckCircle2 } from "lucide-react";
import { FilingPanel } from "../components/coverage/FilingPanel";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useTranslation } from "react-i18next";
import { currentLanguage } from "../i18n";
import { useMeta } from "../lib/seo";
import { track } from "../lib/analytics";
import { InfoBanner } from "../components/Layout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";

const COMMON_ITEMS = ["Water heater leak", "HVAC not cooling", "Refrigerator compressor", "Plumbing stoppage", "Oven / range failure", "Garage door opener", "Roof leak", "Electrical panel"];

const VERDICTS = {
  likely_covered: { label: "Likely covered", cls: "bg-emerald-50 border-emerald-200 text-emerald-800", Icon: ShieldCheck },
  conditions_apply: { label: "Conditions apply", cls: "bg-amber-50 border-amber-200 text-amber-900", Icon: AlertTriangle },
  likely_excluded: { label: "Likely excluded", cls: "bg-rose-50 border-rose-200 text-rose-800", Icon: XCircle },
};

export default function CoverageCheck() {
  const { cid } = useParams();
  const { user, login } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  useMeta("Coverage check - is your claim covered? Free answer | Denial Decoder", "Check if your claim should be covered before you file. Upload your contract, get a free answer with the exact clauses.");

  const [step, setStep] = useState(cid ? "loading" : "setup");
  const [question, setQuestion] = useState("");
  const [contractMode, setContractMode] = useState("upload");
  const [file, setFile] = useState(null);
  const [pasted, setPasted] = useState("");
  const [check, setCheck] = useState(null);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [newsletter, setNewsletter] = useState(true);
  const [filing, setFiling] = useState(null);
  const [filedForm, setFiledForm] = useState({ claim_number: "", filed_date: "", channel: "" });
  const [filedRecord, setFiledRecord] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!cid) return;
    (async () => {
      try {
        if (user) {
          const { data } = await api.post(`/coverage-check/${cid}/claim`);
          setCheck(data); setStep("result");
          if (data.claim_record_id) setFiledRecord({ id: data.claim_record_id });
          return;
        }
        const { data } = await api.get(`/coverage-check/${cid}`);
        setCheck(data);
        setStep(data.locked ? "locked" : "result");
      } catch {
        toast.error("Coverage check not found");
        setStep("setup");
      }
    })();
  }, [cid, user]);

  const startAndAnalyze = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data: started } = await api.post("/coverage-check/start", { question });
      track("coverage_check_started");
      const fd = new FormData();
      if (contractMode === "upload" && file) fd.append("file", file);
      else fd.append("text", pasted);
      const { data: analyzed } = await api.post(`/coverage-check/${started.id}/analyze`, fd);
      navigate(`/coverage-check/${started.id}`, { replace: true });
      setCheck(analyzed);
      setStep("locked");
      track("coverage_check_completed", { claimType: analyzed.claim_type });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not run the check — try again");
    } finally { setBusy(false); }
  };

  const unlock = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post(`/coverage-check/${check.id}/unlock`, { email, name, newsletter_opt_in: newsletter, language: currentLanguage() });
      if (data.account_exists) {
        toast.info("You already have an account — log in to see your answer.");
        navigate(`/login?next=/coverage-check/${check.id}`);
        return;
      }
      login(data.token, data.user);
      setCheck(data.result);
      setStep("result");
      toast.success("Account created — your contract is saved. Check your email to set a password.");
    } catch (err) { toast.error(err.response?.data?.detail || "Could not unlock"); }
    finally { setBusy(false); }
  };

  const loadFiling = async () => {
    track("file_claim_clicked", { claimType: check.claim_type });
    setBusy(true);
    try {
      const { data } = await api.post(`/coverage-check/${check.id}/filing-helper`);
      setFiling(data);
    } catch (err) { toast.error(err.response?.data?.detail || "Could not build the filing helper"); }
    finally { setBusy(false); }
  };

  const recordFiled = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post(`/coverage-check/${check.id}/claim-filed`, filedForm);
      setFiledRecord(data.record);
      track("claim_filed_recorded", { claimType: check.claim_type });
      toast.success("Claim filed record saved — if it's denied, come back and we'll decode it.");
    } catch (err) { toast.error(err.response?.data?.detail || "Could not save"); }
    finally { setBusy(false); }
  };

  const copyDescription = async () => {
    await navigator.clipboard.writeText(filing.claim_description);
    toast.success("Claim description copied");
  };

  const v = check?.verdict ? VERDICTS[check.verdict] : null;
  const result = check?.result;

  return (
    <div className="max-w-3xl mx-auto px-4 py-12 sm:py-16 fade-in" data-testid="coverage-check-page">
      <p className="text-xs font-semibold uppercase tracking-wider text-emerald-700 mb-2">{t("cc.kicker")}</p>
      <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight mb-2">{t("cc.h1")}</h1>
      <p className="text-slate-600 mb-8">{t("cc.sub")}</p>

      {step === "setup" && (
        <form onSubmit={startAndAnalyze} className="bg-white border border-slate-200 rounded-2xl p-6 sm:p-8 space-y-5" data-testid="cc-setup-form">
          <div>
            <Label>{t("cc.question")}</Label>
            <Textarea rows={3} value={question} onChange={(e) => setQuestion(e.target.value)}
              placeholder={t("cc.questionPh")} data-testid="cc-question-input" />
            <div className="flex flex-wrap gap-2 mt-2">
              {COMMON_ITEMS.map((item) => (
                <button type="button" key={item} onClick={() => setQuestion(`Is my ${item.toLowerCase()} covered?`)}
                  className="text-xs px-3 py-1.5 rounded-full border border-slate-200 text-slate-600 hover:border-emerald-400 hover:text-emerald-700 transition-colors"
                  data-testid={`cc-chip-${item.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>{item}</button>
              ))}
            </div>
          </div>
          <div>
            <Label>{t("cc.addContract")}</Label>
            <p className="text-xs text-slate-500 mt-0.5">Provider and plan are read straight from the document — no forms to fill in.</p>
            <div className="flex gap-2 mb-3 mt-2">
              {[["upload", t("cc.upload")], ["paste", t("cc.paste")]].map(([m, label]) => (
                <button type="button" key={m} onClick={() => setContractMode(m)}
                  className={`text-sm px-4 py-2 rounded-full border ${contractMode === m ? "bg-emerald-600 text-white border-emerald-600" : "border-slate-300 text-slate-600"}`}
                  data-testid={`cc-mode-${m}`}>{label}</button>
              ))}
            </div>
            {contractMode === "upload" ? (
              <label className="flex flex-col items-center justify-center border-2 border-dashed border-slate-300 rounded-2xl p-10 cursor-pointer hover:border-emerald-400 transition-colors" data-testid="cc-upload-drop">
                <Upload size={28} className="text-slate-500 mb-2" />
                <span className="text-sm text-slate-600">{file ? file.name : t("cc.uploadHint")}</span>
                <input type="file" accept=".pdf,.jpg,.jpeg,.png,.heic,.txt" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="cc-file-input" />
              </label>
            ) : (
              <Textarea rows={8} value={pasted} onChange={(e) => setPasted(e.target.value)} placeholder={t("cc.pasteHint")} data-testid="cc-paste-input" />
            )}
          </div>
          <Button type="submit" disabled={busy || question.trim().length < 8 || (contractMode === "upload" ? !file : pasted.trim().length < 50)}
            className="bg-emerald-700 hover:bg-emerald-800 rounded-full px-8" data-testid="cc-start-button">
            {busy ? t("cc.reading") : t("cc.checkCoverage")}
          </Button>
          {busy && <p className="text-sm text-slate-500 mt-1">{t("cc.reading")}</p>}
        </form>
      )}

      {step === "locked" && check && v && (
        <div className="space-y-6" data-testid="cc-locked-result">
          <div className={`border rounded-2xl p-6 flex items-center gap-4 ${v.cls}`}>
            <v.Icon size={28} />
            <div>
              <p className="font-bold text-lg" data-testid="cc-verdict-badge">{v.label}</p>
              <p className="text-sm opacity-80" data-testid="cc-answer-teaser">{check.answer_teaser}…</p>
            </div>
          </div>
          {(check.provider_name || check.plan_name) && (
            <p className="text-sm text-slate-500" data-testid="cc-detected-details">
              From your contract: <strong>{check.provider_name || "provider not detected"}</strong>
              {check.plan_name ? ` · ${check.plan_name}` : ""}{check.plan_year ? ` · ${check.plan_year}` : ""}
            </p>
          )}
          <div className="bg-white border border-slate-200 rounded-2xl p-6 sm:p-8">
            <h2 className="text-xl font-semibold mb-1">{t("cc.unlockTitle")}</h2>
            <p className="text-sm text-slate-500 mb-5">{t("cc.unlockSub")}</p>
            <form onSubmit={unlock} className="space-y-4 max-w-sm">
              <div><Label>{t("cc.name")}</Label><Input value={name} onChange={(e) => setName(e.target.value)} placeholder={t("auth.namePh")} data-testid="cc-name-input" /></div>
              <div><Label>{t("cc.email")}</Label><Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" data-testid="cc-email-input" /></div>
              <label className="flex items-start gap-3 text-sm text-slate-600">
                <input type="checkbox" checked={newsletter} onChange={(e) => setNewsletter(e.target.checked)} className="mt-1 accent-emerald-600" data-testid="cc-newsletter-checkbox" />
                <span>{t("cc.newsletterOptIn")}</span>
              </label>
              <Button type="submit" disabled={busy} className="bg-emerald-700 hover:bg-emerald-800 rounded-full px-8" data-testid="cc-unlock-button">
                {busy ? t("common.loading") : t("cc.showAnswer")}
              </Button>
            </form>
          </div>
        </div>
      )}

      {step === "result" && result && v && (
        <div className="space-y-6" data-testid="cc-full-result">
          <div className={`border rounded-2xl p-6 flex items-center gap-4 ${v.cls}`}>
            <v.Icon size={28} />
            <div>
              <p className="font-bold text-lg" data-testid="cc-verdict">{v.label}</p>
              <p className="text-sm opacity-90 mt-1" data-testid="cc-answer">{result.answer}</p>
            </div>
          </div>
          <InfoBanner />
          <section className="bg-white border border-slate-200 rounded-2xl p-6" data-testid="cc-clauses">
            <h2 className="text-lg font-semibold mb-3">{t("cc.clausesTitle")}</h2>
            <div className="space-y-3">
              {(result.clauses || []).map((c, i) => (
                <div key={`${c.section}-${(c.quote || "").slice(0, 24)}`} className="border-l-4 border-emerald-500 bg-slate-50 rounded-r-xl p-4" data-testid={`cc-clause-${i}`}>
                  <p className="text-xs font-semibold text-slate-500 mb-1">{c.section}{c.page ? ` · page ${c.page}` : ""}</p>
                  <p className="text-sm italic text-slate-700">"{c.quote}"</p>
                  <p className="text-sm text-slate-600 mt-1">{c.relevance}</p>
                </div>
              ))}
            </div>
          </section>
          {result.conditions?.length > 0 && (
            <section className="bg-white border border-slate-200 rounded-2xl p-6" data-testid="cc-conditions">
              <h2 className="text-lg font-semibold mb-3">{t("cc.conditionsTitle")}</h2>
              <ul className="space-y-2 text-sm text-slate-600">{result.conditions.map((c) => <li key={c.slice(0, 40)} className="flex gap-2"><AlertTriangle size={15} className="text-amber-600 shrink-0 mt-0.5" />{c}</li>)}</ul>
            </section>
          )}
          <section className="bg-white border border-slate-200 rounded-2xl p-6" data-testid="cc-tips">
            <h2 className="text-lg font-semibold mb-3">{t("cc.tipsTitle")}</h2>
            <ul className="space-y-2 text-sm text-slate-600">{(result.before_you_file_tips || []).map((tip) => <li key={tip.slice(0, 40)} className="flex gap-2"><CheckCircle2 size={15} className="text-emerald-600 shrink-0 mt-0.5" />{tip}</li>)}</ul>
            {(check?.prevention_tips || filing?.prevention_tips || result.prevention_tips) && (
              <div className="mt-4 bg-emerald-50 border border-emerald-200 rounded-xl p-4" data-testid="cc-prevention-tips">
                <p className="text-sm font-semibold text-emerald-900 mb-2">Prevention playbook — for next time</p>
                <ul className="space-y-1.5 text-sm text-emerald-900">{(check?.prevention_tips || result.prevention_tips || []).map((tip) => <li key={tip} className="flex gap-2"><CheckCircle2 size={14} className="shrink-0 mt-0.5" />{tip}</li>)}</ul>
              </div>
            )}
            {(result.service_fee_expectation || result.caps_or_limits) && (
              <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                {result.service_fee_expectation && <p className="bg-slate-50 rounded-xl p-3" data-testid="cc-service-fee"><strong>{t("cc.serviceFee")}</strong> {result.service_fee_expectation}</p>}
                {result.caps_or_limits && <p className="bg-slate-50 rounded-xl p-3" data-testid="cc-caps"><strong>{t("cc.caps")}</strong> {result.caps_or_limits}</p>}
              </div>
            )}
          </section>
          <div className="bg-slate-900 text-white rounded-2xl p-6" data-testid="cc-denied-later">
            <p className="font-semibold">{t("cc.deniedLater")}</p>
            <p className="text-sm text-slate-300 mt-1">{t("cc.deniedLaterBody")}</p>
          </div>
          <FilingPanel check={check} filing={filing} filedRecord={filedRecord} busy={busy}
            loadFiling={loadFiling} filedForm={filedForm} setFiledForm={setFiledForm}
            recordFiled={recordFiled} copyDescription={copyDescription} onGoDashboard={() => navigate("/dashboard")} />
        </div>
      )}
      {step === "loading" && <p className="text-slate-500">Loading…</p>}
      <p className="text-xs text-slate-500 mt-10">{t("cc.haveaDenial")} <Link to="/check" className="text-emerald-700 font-medium hover:underline" data-testid="cc-decode-link">{t("cc.decodeLink")}</Link>.</p>
    </div>
  );
}
